import React, { useMemo, useCallback } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { FieldDefinition, FormSchema, SectionDefinition } from './DynamicForm.form.fields';
import { formatFieldValue, formatSubmittedTimestamp } from './DynamicForm.form.utils';
import { RepeatableFormTable } from './DynamicForm.repeatable.table';
import {
  blocksFromFormData,
  type RepeatableBlock,
  type RepeatableTemplate,
} from './DynamicForm.repeatable.utils';
import {
  FormContainer,
  SubmittedWrapper,
  SubmittedHeader,
  SubmittedHeaderContent,
  SubmittedTitle,
  SubmittedToggleIcon,
  SubmittedContent,
  SubmittedFieldGrid,
  SubmittedFieldRow,
  SubmittedFieldLabel,
  SubmittedFieldValue,
  SubmittedEmptyState,
  SubmittedSectionContainer,
  SubmittedSectionTitle,
  SubmittedSectionBox,
  ActionButtonContainer,
  ActionButton,
} from './DynamicForm.styles.index';

const CONSTANTS = {
  MESSAGES: {
    EDIT: 'Edit',
    DOWNLOAD_PDF: 'Download PDF',
    DOWNLOADING_PDF: 'Downloading PDF...',
  },
} as const;

interface SubmittedViewProps {
  formConfig: FormSchema;
  formData: Record<string, unknown>;
  isDraft?: boolean;
  isSubmitted?: boolean;
  isSubmitting?: boolean;
  showButtons?: boolean;
  onEdit?: () => void;
  onDownloadPdf?: () => Promise<void>;
  isDownloadingPdf?: boolean;
  submittedAt?: string | number | Date;
  draftAt?: string | number | Date;
  className?: string;
  style?: React.CSSProperties;
  sections: SectionDefinition[];
  fieldsBySection: Map<string, FieldDefinition[]>;
  fieldsWithoutSection: FieldDefinition[];
  repeatableTemplate: RepeatableTemplate | null;
}

const renderSubmittedField = (field: FieldDefinition, formData: Record<string, unknown>): React.ReactNode => {
  const isHiddenSystemField =
    (field.type === 'static' || field.type === 'variable') && field.visibleToUser === false;
  if (isHiddenSystemField) {
    return null;
  }

  // Try field name first, then field label (for backwards compatibility with drafts saved with label keys)
  const fieldValue = formData[field.name] ?? formData[field.label];
  const displayValue = formatFieldValue(field, fieldValue);

  return (
    <SubmittedFieldRow key={field.id}>
      <SubmittedFieldLabel>
        {field.label}
        {field.required && <span className="required-asterisk">*</span>}
      </SubmittedFieldLabel>
      <SubmittedFieldValue>{displayValue}</SubmittedFieldValue>
    </SubmittedFieldRow>
  );
};

const SubmittedExpandedContent: React.FC<{
  formData: Record<string, unknown>;
  metaText: string;
  hasSectionContent: boolean;
  hasStandaloneFields: boolean;
  hasContent: boolean;
  renderedSections: React.ReactNode[];
  fieldsWithoutSection: FieldDefinition[];
  contentId: string;
  onEdit?: () => void;
  onDownloadPdf?: () => Promise<void>;
  isDownloadingPdf?: boolean;
  showButtons?: boolean;
  isDraft?: boolean;
  isSubmitting?: boolean;
  repeatableTemplate: RepeatableTemplate | null;
  repeatableBlocks: RepeatableBlock[] | null;
}> = ({
  formData,
  metaText,
  hasSectionContent,
  hasStandaloneFields,
  hasContent,
  renderedSections,
  fieldsWithoutSection,
  contentId,
  onEdit,
  onDownloadPdf,
  isDownloadingPdf = false,
  showButtons,
  isDraft,
  isSubmitting,
  repeatableTemplate,
  repeatableBlocks,
}) => {
  let repeatableOrSectionContent: React.ReactNode;
  if (repeatableTemplate) {
    repeatableOrSectionContent = (
      <SubmittedSectionBox>
        <RepeatableFormTable template={repeatableTemplate} blocks={repeatableBlocks ?? []} />
      </SubmittedSectionBox>
    );
  } else if (hasSectionContent) {
    repeatableOrSectionContent = renderedSections;
  } else {
    repeatableOrSectionContent = (
      <SubmittedSectionBox>
        <SubmittedFieldGrid>
          {fieldsWithoutSection.map((field) => renderSubmittedField(field, formData))}
        </SubmittedFieldGrid>
      </SubmittedSectionBox>
    );
  }

  return (
    <SubmittedContent id={contentId}>
      <SubmittedSectionBox>
        <SubmittedSectionContainer>
          {!!metaText && (
            <SubmittedFieldGrid>
              <SubmittedFieldRow>
                <SubmittedFieldValue>{metaText}</SubmittedFieldValue>
              </SubmittedFieldRow>
            </SubmittedFieldGrid>
          )}
        </SubmittedSectionContainer>
      </SubmittedSectionBox>
      {hasContent ? (
        <>
          {repeatableOrSectionContent}
          {!repeatableTemplate && hasSectionContent && hasStandaloneFields && (
            <SubmittedSectionBox>
              <SubmittedFieldGrid>
                {fieldsWithoutSection.map((field) => renderSubmittedField(field, formData))}
              </SubmittedFieldGrid>
            </SubmittedSectionBox>
          )}
        </>
      ) : (
        <SubmittedEmptyState>No form data available</SubmittedEmptyState>
      )}
      {showButtons && ((onEdit && isDraft) || onDownloadPdf) && (
        <ActionButtonContainer>
          {onEdit && isDraft && (
            <ActionButton
              variant="primary"
              onClick={() => {
                onEdit();
              }}
              disabled={isSubmitting || isDownloadingPdf}
              type="button"
              icon={EditIcon}
            >
              {CONSTANTS.MESSAGES.EDIT}
            </ActionButton>
          )}
          {onDownloadPdf && (
            <ActionButton
              variant="tertiary"
              onClick={() => {
                onDownloadPdf().catch(() => undefined);
              }}
              disabled={isSubmitting || isDownloadingPdf}
              type="button"
              icon={DownloadIcon}
            >
              {isDownloadingPdf ? CONSTANTS.MESSAGES.DOWNLOADING_PDF : CONSTANTS.MESSAGES.DOWNLOAD_PDF}
            </ActionButton>
          )}
        </ActionButtonContainer>
      )}
    </SubmittedContent>
  );
};

export const SubmittedView: React.FC<SubmittedViewProps> = ({
  formConfig,
  formData,
  isDraft = false,
  isSubmitted = false,
  isSubmitting = false,
  showButtons = true,
  onEdit,
  onDownloadPdf,
  isDownloadingPdf = false,
  submittedAt,
  draftAt,
  className,
  style,
  sections,
  fieldsBySection,
  fieldsWithoutSection,
  repeatableTemplate,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const formattedSubmittedAt = useMemo(() => formatSubmittedTimestamp(submittedAt), [submittedAt]);
  const formattedDraftAt = useMemo(() => formatSubmittedTimestamp(draftAt), [draftAt]);

  const summaryTitle = useMemo(() => {
    const baseTitle = formConfig.name || (isDraft ? 'Draft Form' : 'Submitted Form');
    return isDraft ? `${baseTitle} (Draft)` : `${baseTitle} submitted`;
  }, [formConfig.name, isDraft]);

  const contentId = useMemo(() => {
    return isDraft ? `draft-form-content-${formConfig.id}` : `submitted-form-content-${formConfig.id}`;
  }, [formConfig.id, isDraft]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const renderedSections = useMemo(() => {
    return sections.map((section) => {
      const sectionFields = fieldsBySection.get(section.id) || [];
      if (sectionFields.length === 0) return null;

      return (
        <SubmittedSectionBox key={section.id}>
          <SubmittedSectionContainer>
            <SubmittedSectionTitle>{section.title}</SubmittedSectionTitle>
            <SubmittedFieldGrid>
              {sectionFields.map((field) => renderSubmittedField(field, formData))}
            </SubmittedFieldGrid>
          </SubmittedSectionContainer>
        </SubmittedSectionBox>
      );
    });
  }, [sections, fieldsBySection, formData]);

  const repeatableBlocks = useMemo(() => {
    if (!repeatableTemplate) {
      return null;
    }
    return blocksFromFormData(repeatableTemplate, formData);
  }, [repeatableTemplate, formData]);

  const hasSectionContent = renderedSections.some((section) => section !== null);
  const hasStandaloneFields = fieldsWithoutSection.length > 0;
  const hasContent = !!repeatableTemplate || hasSectionContent || hasStandaloneFields;

  let metaText: string;
  if (isDraft) {
    metaText = formattedDraftAt ? `Draft saved: ${formattedDraftAt}` : 'Draft';
  } else {
    metaText = formattedSubmittedAt ? `Date: ${formattedSubmittedAt}` : 'Date unavailable';
  }

  return (
    <FormContainer
      className={className}
      style={style}
    >
      <SubmittedWrapper>
        <SubmittedHeader
          type="button"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          expanded={isExpanded}
        >
          <SubmittedHeaderContent>
            <SubmittedTitle>{summaryTitle}</SubmittedTitle>
          </SubmittedHeaderContent>
          <SubmittedToggleIcon expanded={isExpanded}>
            <ExpandMoreIcon fontSize="small" />
          </SubmittedToggleIcon>
        </SubmittedHeader>

        {isExpanded && (
          <SubmittedExpandedContent
            formData={formData}
            metaText={metaText}
            hasSectionContent={hasSectionContent}
            hasStandaloneFields={hasStandaloneFields}
            hasContent={hasContent}
            renderedSections={renderedSections}
            fieldsWithoutSection={fieldsWithoutSection}
            contentId={contentId}
            onEdit={onEdit}
            onDownloadPdf={onDownloadPdf}
            isDownloadingPdf={isDownloadingPdf}
            showButtons={showButtons}
            isDraft={isDraft}
            isSubmitting={isSubmitting}
            repeatableTemplate={repeatableTemplate}
            repeatableBlocks={repeatableBlocks}
          />
        )}
      </SubmittedWrapper>
    </FormContainer>
  );
};
